'use client'

import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { setCookie } from '@/src/utils/browser'
import { ClevertapTextBanner } from './clevertapTextBanner'
import { HOME_BANNER_HEIGHT_COOKIE, HOME_BANNER_HEIGHT_TTL_DAYS } from './reservation'

/** CleverTap KV topic for the home page-load text banner. */
const TOPIC = 'Home_Pageload'

interface BannerData {
    title?: string
    subtitle?: string
    url?: string
    /** Raw event.detail — passed back to CleverTap for viewed/clicked events. */
    raw: unknown
}

interface ClevertapTextBannerWrapperProps {
    reservedHeight?: number
}

/**
 * Logic layer for the CleverTap web-native-display (KV) home banner.
 */
const ClevertapTextBannerWrapper: React.FC<ClevertapTextBannerWrapperProps> = ({ reservedHeight = 0 }) => {
    const t = useTranslations('common')
    const [data, setData] = useState<BannerData | null>(null)
    const bannerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent).detail
            const kv = detail?.kv || {}
            if (kv.topic !== TOPIC) return
            setData({ title: kv.title, subtitle: kv.subtitle, url: kv.url, raw: detail })
            ;(window as any).clevertap?.renderNotificationViewed?.(detail)
        }
        document.addEventListener('CT_web_native_display', handler)
        return () => document.removeEventListener('CT_web_native_display', handler)
    }, [])

    const hasBanner = !!data && (!!data.title || !!data.subtitle)

    useEffect(() => {
        if (!hasBanner || !bannerRef.current) return
        const height = Math.round(bannerRef.current.getBoundingClientRect().height)
        if (height > 0) setCookie(HOME_BANNER_HEIGHT_COOKIE, String(height), HOME_BANNER_HEIGHT_TTL_DAYS)
    }, [hasBanner])

    const handleViewDetails = useCallback(() => {
        if (!data) return
        ;(window as any).clevertap?.renderNotificationClicked?.(data.raw)
        if (data.url) window.open(data.url, '_blank', 'noopener,noreferrer')
    }, [data])

    if (!hasBanner) {
        return reservedHeight > 0 ? <div aria-hidden style={{ minHeight: reservedHeight }} /> : null
    }

    return (
        <div ref={bannerRef} style={{ display: 'flow-root' }}>
            <ClevertapTextBanner
                title={data?.title ?? ''}
                subTitle={data?.subtitle ?? ''}
                viewDetailsLabel={t('viewDetails')}
                showViewDetails={!!data?.url}
                onViewDetails={handleViewDetails}
            />
        </div>
    )
}

export { ClevertapTextBannerWrapper }
export default ClevertapTextBannerWrapper
